import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/oraya-api";
import { useCommand, useRealtime } from "@/context/CommandContext";
import { Panel, Field, inputCls, CCButton, EmptyState, LoadingState, KpiCard, StatusBadge } from "@/command/ccUi";

export const inr = (n) => (n == null || n === "" ? "—" : `₹${Number(n).toLocaleString("en-IN")}`);

const GOOD = ["Active", "Operational", "Won", "Filed", "Paid", "Done", "Completed"];
const WARN = ["Prospect", "Degraded", "Proposal", "Negotiation", "Qualified", "In Progress", "Upcoming", "Pending", "To Do"];
const BAD = ["Down", "Lost", "Overdue", "Rejected", "Blocked"];
export function statusKind(s) {
  if (GOOD.includes(s)) return "healthy";
  if (BAD.includes(s)) return "critical";
  if (WARN.includes(s)) return "pending";
  return "info";
}

function cell(r, field, kind) {
  const v = r[field];
  if (kind === "money") return inr(v);
  if (kind === "pct") return v == null ? "—" : `${v}%`;
  if (kind === "badge") return <StatusBadge status={statusKind(v)} label={v || "—"} />;
  if (kind === "progress") return (
    <div className="flex items-center gap-2 min-w-[110px]">
      <div className="flex-1 h-1.5 rounded-full bg-hairline/15 overflow-hidden"><div className="h-full bg-oraya-accent" style={{ width: `${v || 0}%` }} /></div>
      <span className="text-xs text-subtle">{v || 0}%</span>
    </div>
  );
  return v || v === 0 ? v : "—";
}

export function CrudModule({ title, subtitle, module, endpoint, entity, columns, fields, kpis, summaryPath, defaults = {}, hideKpis }) {
  const { can } = useCommand();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const canCreate = can(module, "CREATE") || can(module, "MANAGE");
  const canEdit = can(module, "EDIT") || can(module, "MANAGE");
  const canDelete = can(module, "DELETE") || can(module, "MANAGE");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const reqs = [api.get(endpoint)];
      if (summaryPath) reqs.push(api.get(summaryPath));
      const res = await Promise.all(reqs);
      setRows(res[0].data);
      if (summaryPath) setSummary(res[1].data);
    } catch {} finally { setLoading(false); }
  }, [endpoint, summaryPath]);
  useEffect(() => { load(); }, [load]);
  useRealtime([module], load);

  const numericFields = fields.filter(([, , t]) => t === "number").map(([f]) => f);
  const save = async () => {
    try {
      const payload = { ...editing };
      numericFields.forEach((f) => { if (payload[f] !== undefined && payload[f] !== "") payload[f] = Number(payload[f]); });
      if (payload.id) await api.put(`${endpoint}/${payload.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this record?")) return; try { await api.delete(`${endpoint}/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  const kpiList = kpis ? kpis(rows, summary) : [];

  return (
    <div>
      {title && <h1 className="display text-2xl md:text-3xl font-light text-ink">{title}</h1>}
      {subtitle && <p className="text-subtle mt-1 mb-6">{subtitle}</p>}

      {!hideKpis && kpiList.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {kpiList.map((k) => <KpiCard key={k.label} label={k.label} value={k.value} prefix={k.prefix || ""} empty={k.empty} />)}
        </div>
      )}

      <Panel testid={`${entity}-panel`}
        action={canCreate && <CCButton onClick={() => setEditing({ ...defaults })} testid={`${entity}-add`}><Plus className="w-4 h-4" /> Add {entity}</CCButton>}>
        {loading ? <LoadingState /> : rows.length === 0 ? (
          <EmptyState title={`No ${entity}s yet`} hint={canCreate ? "Add the first record." : undefined} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-subtle border-b border-hairline/[0.08]">
                {columns.map(([f, l]) => <th key={f} className="px-3 py-3 font-medium">{l}</th>)}
                {(canEdit || canDelete) && <th className="px-3 py-3 text-right">Actions</th>}
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-hairline/[0.05] text-ink" data-testid={`${entity}-row-${r.id}`}>
                    {columns.map(([f, l, kind]) => <td key={f} className="px-3 py-3">{cell(r, f, kind)}</td>)}
                    {(canEdit || canDelete) && <td className="px-3 py-3 text-right whitespace-nowrap">
                      {canEdit && <button onClick={() => setEditing({ ...r })} className="text-subtle hover:text-ink p-1.5" data-testid={`${entity}-edit-${r.id}`}><Pencil className="w-4 h-4" /></button>}
                      {canDelete && <button onClick={() => del(r.id)} className="text-subtle hover:text-red-500 p-1.5" data-testid={`${entity}-del-${r.id}`}><Trash2 className="w-4 h-4" /></button>}
                    </td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setEditing(null)} data-testid={`${entity}-modal`}>
          <div className="glass-heavy rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="display text-lg font-medium text-ink mb-4">{editing.id ? "Edit" : "Add"} {entity}</h3>
            <div className="grid grid-cols-2 gap-4">
              {fields.map(([f, l, type, opts]) => (
                <Field key={f} label={l}>
                  {type === "select" ? (
                    <select className={inputCls} value={editing[f] ?? opts[0]} onChange={(e) => setEditing({ ...editing, [f]: e.target.value })} data-testid={`${entity}-field-${f}`}>
                      {opts.map((o) => <option key={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input type={type} className={inputCls} value={editing[f] ?? ""} onChange={(e) => setEditing({ ...editing, [f]: e.target.value })} data-testid={`${entity}-field-${f}`} />
                  )}
                </Field>
              ))}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEditing(null)} className="text-sm text-subtle hover:text-ink px-4 py-2">Cancel</button>
              <CCButton onClick={save} testid={`${entity}-save`}>Save</CCButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
